import React, { useEffect, useMemo, useState } from 'react';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';

import { Selector, Option } from './Selector';

export interface PurchaserSelectorProps {
  value?: string;
  teamIds?: string[];
  budgetId?: string;
  readOnly?: boolean;
  className?: string;
  clearable?: boolean;
  role: string;
  parentProjectId?: string;
  companyId?: string;
  onChange?: (option: Option) => void;
}

export default (props: PurchaserSelectorProps) => {
  const pok = usePokCore();
  const [name, setName] = useState<string>('');

  useEffect(() => {
    if (props.value) {
      void pok.purchasers.getById(props.value).then(purchaser => {
        setName(
          `${purchaser.name} (${
            purchaser.client.shortname
              ? purchaser.client.shortname
              : purchaser.client.name
          })`,
        );
      });
    } else {
      setName('');
    }
  }, [pok.purchasers, props.value]);

  const provider = useMemo(
    () => (text: string) => {
      if (props.role === PermissionsEnum.Projects) {
        if (props.budgetId) {
          return pok.purchasers.getOptionsByTeamIdsBudgetIdAndName(
            props.teamIds || [],
            props.budgetId,
            text,
            props.parentProjectId,
          );
        }

        return pok.purchasers.getOptionsByTeamIdsAndName(
          props.teamIds || [],
          text,
          props.parentProjectId,
        );
      } else if (props.companyId) {
        return pok.purchasers.getOptionsByCompanyId(props.companyId, text);
      } else {
        return pok.purchasers.getOptionsByTeamIdsAndNameForRoleWithoutTeams(
          props.teamIds || [],
          text,
        );
      }
    },
    [
      pok.purchasers,
      props.role,
      props.budgetId,
      props.teamIds,
      props.parentProjectId,
      props.companyId,
    ],
  );

  return (
    <Selector
      uniqueKey={
        'purchasers-' +
        JSON.stringify(props.budgetId) +
        JSON.stringify(props.companyId) +
        JSON.stringify(name) +
        JSON.stringify(provider) +
        props.teamIds?.join('_')
      }
      className={props.className}
      readOnly={props.readOnly}
      provider={provider}
      creatable={false}
      clearable={props.clearable}
      value={name}
      valueToShow={true}
      onChange={option => props.onChange && props.onChange(option as Option)}
    />
  );
};
